import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useManagementAPI } from '@/hooks/useManagementAPI';
import { CompanyHeroSection, CompanyHeroSkeleton } from './components/CompanyHeroSection';
import { PageSection } from './components/PageSection';
import { CompanyArchiveSection } from './components/CompanyArchiveSection';
import { useToast } from '@/hooks/use-toast';

const PricingSaasCompanyPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { toast } = useToast();
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);

  const {
    company,
    pages,
    categories,
    loading,
    error,
    updateCompany,
    updatePage,
    archiveAllPages,
    unarchiveAllPages,
    refetch
  } = useManagementAPI(slug || '');

  // Auto-select first non-archived page when pages load
  useEffect(() => {
    if (Array.isArray(pages) && pages.length > 0 && !selectedPageId) {
      const firstVisiblePage = pages.find(p => !p.archived);
      if (firstVisiblePage) {
        setSelectedPageId(firstVisiblePage.id);
      }
    }
  }, [pages, selectedPageId]);

  if (!slug) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50 p-6">
          <div className="text-center text-gray-500">
            No company slug provided
          </div>
        </div>
      </div>
    );
  }

  const handleUpdateSuccess = () => {
    toast({
      title: 'Changes saved',
      description: 'Company information has been updated successfully.',
    });
  };

  const handleUpdateError = (errorMessage: string) => {
    toast({
      title: 'Error saving changes',
      description: errorMessage,
      variant: 'destructive',
    });
  };

  const handlePageUpdateSuccess = () => {
    toast({
      title: 'Page saved',
      description: 'Page information has been updated successfully.',
    });
  };

  const handlePageUpdateError = (errorMessage: string) => {
    toast({
      title: 'Error saving page',
      description: errorMessage,
      variant: 'destructive',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-[1400px] mx-auto px-8 py-8">
        {/* Loading State */}
        {loading && <CompanyHeroSkeleton />}

        {/* Error State */}
        {!loading && error && (
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-red-700 mb-2">Error loading company</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={refetch}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Company Hero Section */}
        {!loading && !error && company && (
          <>
            <CompanyHeroSection
              company={company}
              categories={categories}
              loading={false}
              onUpdate={updateCompany}
              onUpdateSuccess={handleUpdateSuccess}
              onUpdateError={handleUpdateError}
            />

            {/* Page Section - Combined tabs and content */}
            <div className="mt-6">
              <PageSection
                pages={pages}
                selectedPageId={selectedPageId}
                onSelectPage={setSelectedPageId}
                loading={false}
                companySlug={slug}
                onUpdatePage={updatePage}
                onUpdateSuccess={handlePageUpdateSuccess}
                onUpdateError={handlePageUpdateError}
              />
            </div>

            {/* Archive Section - At the bottom */}
            <div className="mt-6">
              <CompanyArchiveSection
                companyName={company.name}
                pages={pages}
                onArchiveAllPages={archiveAllPages}
                onUnarchiveAllPages={unarchiveAllPages}
              />
            </div>
          </>
        )}

        {/* No Data State */}
        {!loading && !error && !company && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-center text-gray-500">
              Company not found
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingSaasCompanyPage;
